import { Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import PageLoader from 'typescript/components/page_loader';
import CheckoutLayout from 'typescript/apps/checkout_v3/checkout_layout';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import { CheckoutFlowProvider } from 'typescript/context/checkout_flow';
import { FeatureFlags } from 'typescript/context/feature_flags';
import { LocaleInfoProvider } from 'typescript/context/locale_info';
import { SavedCardsProvider } from 'typescript/context/saved_cards';
import { MarketManagers } from 'typescript/context/market_managers';
import TranslationsLoader from 'typescript/components/translations_loader';
import CustomQueryClientProvider from 'typescript/providers/custom_query_client_provider';
import { PaymentContextProvider } from 'typescript/context/payment_context';
import { CompleteOrderProvider } from 'typescript/context/complete_order_context';
import { CheckoutExperiments } from 'typescript/context/checkout_experiments';
import ShopErrorBoundary from 'typescript/components/shop_error_boundary';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const CHECKOUT_V3_BASE_PATH = '/shop/hardware/\\w{2}/\\w{2}/checkout/v3';

interface CheckoutV3AppProps {
  orderNumber: string;
  languageCode: LanguageCode;
  storeCode: CountryCode;
  applicationId: string;
  locationId: string;
  recaptchaKey: string;
  trackingData: Record<string, any>;
  featureFlags: Record<string, any>;
  deliveryOptions: Record<string, boolean>;
  experiments: Record<string, any>;
}

const CheckoutV3App = ({
  orderNumber,
  languageCode,
  storeCode,
  applicationId,
  locationId,
  recaptchaKey,
  trackingData,
  featureFlags,
  deliveryOptions,
  experiments,
}: CheckoutV3AppProps) => {
  return (
    <ShopErrorBoundary appName="checkout_v3">
      <Suspense fallback={<PageLoader delayMs={500} />}>
        <CustomQueryClientProvider>
          <TranslationsLoader>
            <MarketManagers>
              <FeatureFlags featureFlags={featureFlags}>
                <CheckoutExperiments experiments={experiments}>
                  <LocaleInfoProvider
                    countryCode={storeCode as CountryCode}
                    languageCode={languageCode as LanguageCode}
                  >
                    <CheckoutFlowProvider
                      orderNumber={orderNumber}
                      applicationId={applicationId}
                      locationId={locationId}
                      recaptchaKey={recaptchaKey}
                      trackingData={trackingData}
                      deliveryOptions={deliveryOptions}
                    >
                      <SavedCardsProvider>
                        <CompleteOrderProvider>
                          <PaymentContextProvider>
                            <BrowserRouter
                              basename={
                                window.location.pathname.match(
                                  CHECKOUT_V3_BASE_PATH,
                                )[0]
                              }
                            >
                              <CheckoutLayout />
                              <ReactQueryDevtools initialIsOpen={false} />
                            </BrowserRouter>
                          </PaymentContextProvider>
                        </CompleteOrderProvider>
                      </SavedCardsProvider>
                    </CheckoutFlowProvider>
                  </LocaleInfoProvider>
                </CheckoutExperiments>
              </FeatureFlags>
            </MarketManagers>
          </TranslationsLoader>
        </CustomQueryClientProvider>
      </Suspense>
    </ShopErrorBoundary>
  );
};

export default CheckoutV3App;
